import React from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import {
  ErrorMessage,
  FileUploader,
  PrimaryLiteButton,
  PrimaryLoadingButton,
  PrimaryNumericField,
  PrimaryOutlinedButton,
  PrimarySelect,
  PrimaryTextField,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { IOption } from 'interfaces';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import {
  CertificateTemplate,
  CertificateValidityUnit,
  ICertificate,
} from '../../../../../interface';
import CertificatePreviewModal from '../certificate-preview-modal';
import {
  buildCertificateRenderRequest,
  getCertificatePreviewFrameHeight,
  renderCertificatePreviewHtmlViaService,
  sanitizeCertificate,
} from '../utils';
import {
  CERTIFICATE_TEMPLATES,
} from '../template-catalog';
import {
  CERTIFICATE_LOGO_LIMIT_MESSAGE,
  CERTIFICATE_SIGNATORY_LIMIT_MESSAGE,
  certificateFieldConfigs,
  MAX_CERTIFICATE_LOGOS,
  MAX_CERTIFICATE_SIGNATORIES,
} from '../validation/certificate-modal-form-validation';

interface CertificateModalFormProps {
  open: boolean;
  title: string,
  onSubmit: SubmitHandler<ICertificate>;
  closeModal: () => void;
  loading: boolean;
  sessionTitle?: string;
  moduleTitles?: string[];
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

const validityUnitOptions: IOption[] = [
  { id: 1, value: CertificateValidityUnit.MONTH, text: 'Month' },
  { id: 2, value: CertificateValidityUnit.YEAR, text: 'Year' },
];

type TemplatePreview = {
  html: string;
  error: string | null;
};

const CertificateModalForm = ({
  open,
  title,
  onSubmit,
  closeModal,
  loading,
  sessionTitle,
  moduleTitles,
  enterprise,
}: CertificateModalFormProps) => {
  const {
    control,
    handleSubmit,
    clearErrors,
    getValues,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ICertificate>();

  const { fields: logoFields, append: appendLogo, remove: removeLogo } = useFieldArray({
    name: 'logos',
    control,
  });
  const {
    fields: signatoryFields,
    append: appendSignatory,
    remove: removeSignatory,
  } = useFieldArray({
    name: 'signatories',
    control,
  });

  const certificateBadge = watch('badge');
  const validity = watch('validity');
  const previewCertificate = watch();
  const logoLimitReached = logoFields.length >= MAX_CERTIFICATE_LOGOS;
  const signatoryLimitReached = signatoryFields.length >= MAX_CERTIFICATE_SIGNATORIES;
  const [openPreviewModal, setOpenPreviewModal] = React.useState(false);

  const sanitizedPreviewCertificate = React.useMemo(
    () => sanitizeCertificate(previewCertificate),
    [previewCertificate],
  );
  const templatePreviewFrameWidth = 1122;
  const templatePreviewScale = 0.16;
  const [templatePickerPreviewByTemplate, setTemplatePickerPreviewByTemplate] = React.useState(
    {} as Record<CertificateTemplate, TemplatePreview>,
  );

  React.useEffect(() => {
    let ignore = false;

    const loadPreviews = async () => {
      if (!open) {
        if (!ignore) {
          setTemplatePickerPreviewByTemplate(
            {} as Record<CertificateTemplate, TemplatePreview>,
          );
        }
        return;
      }

      const entries = await Promise.all(CERTIFICATE_TEMPLATES.map(async (template) => {
        const templateCertificate = sanitizeCertificate({
          title: 'Certificate of Completion',
          description: template.description,
          template: template.value,
          header: template.label,
          logos: [],
          badge: undefined,
          signatories: [],
          validity: undefined,
        });

        const request = buildCertificateRenderRequest({
          certificate: templateCertificate,
          enterprise,
          sessionTitle,
          moduleTitles,
        });

        try {
          const html = await renderCertificatePreviewHtmlViaService({
            ...request,
            options: {
              ...request.options,
              previewLayout: 'stacked',
            },
          });

          return [template.value, { html, error: null }] as const;
        } catch (error) {
          return [template.value, {
            html: '',
            error: error instanceof Error ? error.message : 'Failed to load template preview',
          }] as const;
        }
      }));

      if (!ignore) {
        const previewMap = Object.fromEntries(entries);
        setTemplatePickerPreviewByTemplate(previewMap);
      }
    };

    loadPreviews();
    return () => {
      ignore = true;
    };
  }, [enterprise, moduleTitles, open, sessionTitle]);

  const handleForm = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmit)();
  };

  const handleUpload = (
    fieldPath: `logos.${number}.logoUrl` | 'badge.imageUrl' | `signatories.${number}.signatureImageUrl`,
  ) => (res: UploadResponse) => {
    clearErrors(fieldPath);
    setValue(fieldPath, res.url, { shouldDirty: true, shouldValidate: true });
    if (res.statusCode !== 201) {
      setError(fieldPath, { type: 'custom', message: res.message });
    }
  };

  const handleRemoveUpload = (
    fieldPath: `logos.${number}.logoUrl` | 'badge.imageUrl' | `signatories.${number}.signatureImageUrl`,
  ) => () => {
    setValue(fieldPath, '', { shouldDirty: true, shouldValidate: true });
  };

  const handleAddLogo = () => {
    if (logoLimitReached) {
      return;
    }
    appendLogo({ title: '', logoUrl: '' });
  };

  const handleAddSignatory = () => {
    if (signatoryLimitReached) {
      return;
    }
    appendSignatory({ name: '', designation: '', signatureImageUrl: '' });
  };

  return (
    <form onSubmit={handleForm}>
      <Typography variant="h4" className="form-item">
        {title}
      </Typography>

      <div className="form-item">
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} label="Certificate title" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.title} />
              </Box>
            </>
          )}
        />
      </div>

      <div className="form-item">
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} multiline rows={4} label="Certificate description" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.description} />
              </Box>
            </>
          )}
        />
      </div>

      <div className="form-item">
        <Typography variant="h4" sx={{ mb: 1.5 }}>Certificate template</Typography>
        <Controller
          control={control}
          name="template"
          render={({ field, fieldState }) => {
            const selectedValue = field.value || CERTIFICATE_TEMPLATES[0].value;
            return (
              <>
                <RadioGroup
                  value={selectedValue}
                  onChange={(event) => field.onChange(event.target.value as CertificateTemplate)}
                >
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                  >
                    {CERTIFICATE_TEMPLATES.map((template) => {
                      const preview = templatePickerPreviewByTemplate[template.value];
                      const isSelected = selectedValue === template.value;
                      const templatePreviewFrameHeight = getCertificatePreviewFrameHeight(
                        template.value,
                      );
                      return (
                        <Box
                          key={template.value}
                          sx={{
                            border: '1px solid',
                            borderColor: isSelected ? '#c27171' : '#d9e2ec',
                            borderRadius: '12px',
                            padding: '12px',
                            background: '#fff',
                            boxShadow: isSelected ? '0 0 0 1px rgb(194 113 113 / 20%)' : 'none',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          }}
                        >
                          <FormControlLabel
                            value={template.value}
                            control={<Radio />}
                            label={template.label}
                            sx={{ mb: 1.25 }}
                          />
                          <Box
                            sx={{
                              minHeight: '272px',
                              marginBottom: '10px',
                              padding: '10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '10px',
                              background: '#eef2f7',
                              overflow: 'hidden',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'flex-start',
                            }}
                          >
                            {preview?.error && (
                              <Box
                                sx={{
                                  width: '100%',
                                  minHeight: '148px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant="caption" color="error">
                                  {preview.error}
                                </Typography>
                              </Box>
                            )}

                            {!preview?.error && preview?.html && (
                              <Box
                                sx={{
                                  flex: '0 0 auto',
                                  width: `${Math.round(templatePreviewFrameWidth * templatePreviewScale)}px`,
                                  height: `${Math.round(templatePreviewFrameHeight * templatePreviewScale)}px`,
                                }}
                              >
                                <iframe
                                  title={`${template.label} option preview`}
                                  srcDoc={preview.html}
                                  style={{
                                    width: `${templatePreviewFrameWidth}px`,
                                    height: `${templatePreviewFrameHeight}px`,
                                    border: 0,
                                    transform: `scale(${templatePreviewScale})`,
                                    transformOrigin: 'top left',
                                    pointerEvents: 'none',
                                    background: '#fff',
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </RadioGroup>
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
              </>
            );
          }}
        />
      </div>

      <div className="form-item">
        <Controller
          control={control}
          name="header"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} label="Certificate header" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.header} />
              </Box>
            </>
          )}
        />
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate logos</Typography>
          </Grid>
          <Grid item>
            <PrimaryOutlinedButton
              disabled={logoLimitReached}
              startIcon={<AddIcon />}
              onClick={handleAddLogo}
            >
              Add logo
            </PrimaryOutlinedButton>
          </Grid>
        </Grid>
        <ErrorMessage>
          {(errors?.logos as { message?: string } | undefined)?.message
            || (logoLimitReached ? CERTIFICATE_LOGO_LIMIT_MESSAGE : '')}
        </ErrorMessage>

        {logoFields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              mb: 3,
              border: '1px solid #E5E5E5',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="h4">
                  Logo #
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => removeLogo(index)} aria-label={`remove-logo-${index}`}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Logo image</Typography>
              <FileUploader
                handleFile={handleUpload(`logos.${index}.logoUrl`)}
                handleRemove={handleRemoveUpload(`logos.${index}.logoUrl`)}
                currentContentUrl={getValues(`logos.${index}.logoUrl`) || ''}
                scenario={UploadScenarios.sessionCertificateLogo}
                currentMediaType="image"
              />
              <ErrorMessage>{errors?.logos?.[index]?.logoUrl?.message}</ErrorMessage>
            </Box>

            <Controller
              control={control}
              name={`logos.${index}.title`}
              render={({ field: logoField, fieldState }) => (
                <>
                  <PrimaryTextField {...logoField} label="Logo title" />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.5,
                  }}
                  >
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                    <CharacterCountDisplay value={logoField.value || ''} config={certificateFieldConfigs.logoTitle} />
                  </Box>
                </>
              )}
            />
          </Box>
        ))}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate badge</Typography>
          </Grid>
          <Grid item>
            {certificateBadge ? (
              <PrimaryOutlinedButton
                startIcon={<CloseIcon />}
                onClick={() => {
                  setValue('badge', undefined, { shouldDirty: true, shouldValidate: false });
                  clearErrors('badge');
                }}
              >
                Remove badge
              </PrimaryOutlinedButton>
            ) : (
              <PrimaryOutlinedButton
                startIcon={<AddIcon />}
                onClick={() => setValue('badge', { title: '', description: '', imageUrl: '' }, { shouldDirty: true })}
              >
                Add badge
              </PrimaryOutlinedButton>
            )}
          </Grid>
        </Grid>

        {certificateBadge && (
          <Box sx={{ border: '1px solid #E5E5E5', borderRadius: '10px', p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Badge image</Typography>
              <FileUploader
                handleFile={handleUpload('badge.imageUrl')}
                handleRemove={handleRemoveUpload('badge.imageUrl')}
                currentContentUrl={getValues('badge.imageUrl') || ''}
                scenario={UploadScenarios.sessionCertificateBadge}
                currentMediaType="image"
              />
              <ErrorMessage>{errors?.badge?.imageUrl?.message}</ErrorMessage>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Controller
                control={control}
                name="badge.title"
                render={({ field, fieldState }) => (
                  <>
                    <PrimaryTextField {...field} label="Badge title" />
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 0.5,
                    }}
                    >
                      <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                      <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.badgeTitle} />
                    </Box>
                  </>
                )}
              />
            </Box>

            <Controller
              control={control}
              name="badge.description"
              render={({ field, fieldState }) => (
                <>
                  <PrimaryTextField {...field} multiline rows={3} label="Badge description" />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.5,
                  }}
                  >
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                    <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.badgeDescription} />
                  </Box>
                </>
              )}
            />
          </Box>
        )}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate signatories</Typography>
          </Grid>
          <Grid item>
            <PrimaryOutlinedButton
              disabled={signatoryLimitReached}
              startIcon={<AddIcon />}
              onClick={handleAddSignatory}
            >
              Add signatory
            </PrimaryOutlinedButton>
          </Grid>
        </Grid>
        <ErrorMessage>
          {(errors?.signatories as { message?: string } | undefined)?.message
            || (signatoryLimitReached ? CERTIFICATE_SIGNATORY_LIMIT_MESSAGE : '')}
        </ErrorMessage>

        {signatoryFields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              mb: 3,
              border: '1px solid #E5E5E5',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="h4">
                  Signatory #
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => removeSignatory(index)} aria-label={`remove-signatory-${index}`}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Signature image</Typography>
              <FileUploader
                handleFile={handleUpload(`signatories.${index}.signatureImageUrl`)}
                handleRemove={handleRemoveUpload(`signatories.${index}.signatureImageUrl`)}
                currentContentUrl={getValues(`signatories.${index}.signatureImageUrl`) || ''}
                scenario={UploadScenarios.sessionCertificateSignature}
                currentMediaType="image"
              />
              <ErrorMessage>
                {errors?.signatories?.[index]?.signatureImageUrl?.message}
              </ErrorMessage>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name={`signatories.${index}.name`}
                  render={({ field: signatoryField, fieldState }) => (
                    <>
                      <PrimaryTextField {...signatoryField} label="Name" />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                        <CharacterCountDisplay value={signatoryField.value || ''} config={certificateFieldConfigs.signatoryName} />
                      </Box>
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name={`signatories.${index}.designation`}
                  render={({ field: designationField, fieldState }) => (
                    <>
                      <PrimaryTextField {...designationField} label="Designation" />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                        <CharacterCountDisplay value={designationField.value || ''} config={certificateFieldConfigs.signatoryDesignation} />
                      </Box>
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate validity</Typography>
          </Grid>
          <Grid item>
            {validity ? (
              <PrimaryOutlinedButton
                startIcon={<CloseIcon />}
                onClick={() => {
                  setValue('validity', undefined, { shouldDirty: true, shouldValidate: false });
                  clearErrors('validity');
                }}
              >
                Remove validity
              </PrimaryOutlinedButton>
            ) : (
              <PrimaryOutlinedButton
                startIcon={<AddIcon />}
                onClick={() => setValue('validity', { value: 1, unit: CertificateValidityUnit.MONTH }, { shouldDirty: true })}
              >
                Add validity
              </PrimaryOutlinedButton>
            )}
          </Grid>
        </Grid>

        {validity && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="validity.value"
                render={({ field, fieldState }) => (
                  <>
                    <PrimaryNumericField {...field} label="Validity value" min={1} />
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="validity.unit"
                render={({ field, fieldState }) => (
                  <>
                    <PrimarySelect {...field} label="Validity unit" options={validityUnitOptions} placeholder="Select validity unit" />
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                  </>
                )}
              />
            </Grid>
          </Grid>
        )}
      </div>

      <div className="form-item">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryOutlinedButton type="button" onClick={() => setOpenPreviewModal(true)}>
            Preview certificate
          </PrimaryOutlinedButton>
        </Box>
      </div>

      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLiteButton fullWidth onClick={closeModal}>Cancel</PrimaryLiteButton>
        </Grid>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} fullWidth type="submit">
            Add & continue
          </PrimaryLoadingButton>
        </Grid>
      </Grid>

      <CertificatePreviewModal
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        certificate={sanitizedPreviewCertificate}
        sessionTitle={sessionTitle}
        moduleTitles={moduleTitles}
        enterprise={enterprise}
      />
    </form>
  );
};

CertificateModalForm.defaultProps = {
  sessionTitle: '',
  moduleTitles: [],
  enterprise: null,
};

export default CertificateModalForm;
